import Vue from "vue"
import axios from "@/api/axiosAuthRequired.js"
import uuidv4 from "uuid/v4"
import { HOME_STEP } from "@/constants/auth"
import { DISCORD_SSO_OPTIONS, AIM_LAB_SSO_OPTIONS } from "./auth.constants"
import { oauthLogin, checkAuthStatus } from "@/api/auth.js"
import { disconnectOauth } from "../../api/auth"
import { fetchClientCredentialsWithOneTimeToken, refreshAimlabsToken } from "./auth.apis"
import { getAuthHeaders } from "@/api/auth"
import { openWindow } from "@/utils/window"
import { MIXINS } from "@/utils/mixins"

const state = {
  loading: false,
  error: "",
  oauthProviders: [],
  SSOLinkError: "",
  ssoLoading: false,
  aimlabsAuthListenerBound: false,
  aimlabsAuthListenerCallbackQueue: [],
  homeStep: HOME_STEP.START_TRIAL,
  email: "",
}

const getters = {
  loading: state => state.loading,
  error: state => state.error,
  oauthProviders: state => state.oauthProviders,
  SSOLinkError: state => state.SSOLinkError,
  ssoLoading: state => state.ssoLoading,
  homeStep: state => state.homeStep,
  email: state => state.email,
  isAimLabUser: state => {
    return state.oauthProviders.some(
      provider => provider.provider === "aim-lab"
    )
  },
}

const mutations = {
  setLoading: (state, loading) => (state.loading = loading),
  setError: (state, error) => (state.error = error),
  setOauthProviders: (state, providers) => (state.oauthProviders = providers),
  setSSOLinkError: (state, error) => (state.SSOLinkError = error),
  setSSOLoading: (state, loading) => (state.ssoLoading = loading),
  setAimlabsAuthListenerBound: (state, isBound) => (state.aimlabsAuthListenerBound = isBound),
  addCallbackToAimlabsAuthListenerQueue: (state, callback) => (state.aimlabsAuthListenerCallbackQueue.push(callback)),
  clearAimlabsAuthListenerQueue: (state) => (state.aimlabsAuthListenerCallbackQueue = []),
  setHomeStep: (state, step) => (state.homeStep = step),
  setEmail: (state, email) => (state.email = email),
}

const actions = {
  initTokens (context, { accessToken, refreshToken }) {
    window.localStorage.setItem("client_credentials", accessToken)
    window.localStorage.setItem("refresh_token", refreshToken)
  },
  initFacebookSdk () {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: process.env.FACEBOOK_APP_ID,
        xfbml: true,
        version: "v2.7",
      })
      window.FB.AppEvents.logPageView()
      // console.log("FB SDK was initialized as mixin")
    }
    ;(function (d, s, id) {
      let js = d.getElementsByTagName(s)[0]
      let fjs = d.getElementsByTagName(s)[0]
      if (d.getElementById(id)) {
        return
      }
      js = d.createElement(s)
      js.id = id
      js.src = "//connect.facebook.net/en_US/sdk.js"
      fjs.parentNode.insertBefore(js, fjs)
    })(document, "script", "facebook-jssdk")
  },
  loginWithFacebook ({ dispatch, commit }) {
    return new Promise(async (resolve, reject) => {
      commit("setSSOLoading", true)
      window.FB.login(
        response => {
          if (response.authResponse) {
            window.FB.getLoginStatus(async function (response) {
              const token = response.authResponse.accessToken
              const body = new FormData()
              body.append("token", token)
              try {
                const res = await axios.post(
                  `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/facebook/connect.php`,
                  body
                )
                const tokens = {
                  accessToken: res.data.access_token,
                  refreshToken: res.data.refresh_token,
                }
                await dispatch("initTokens", tokens)
                if (res.data.status === "success") {
                  await dispatch("trackUserLoginEvent")
                  MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
                    platform_name: "Facebook",
                  })
                  // await dispatch("oauthLogin", {
                  //   provider: "facebook",
                  //   token,
                  // })
                  await Promise.all([
                    dispatch("User/loadUser", null, { root: true }),
                    dispatch("User/loadUserCapabilities", null, { root: true }),
                    dispatch("User/Profile/loadSettings", null, { root: true }),
                  ])
                  resolve(
                    res.data.user_id ? "register_success" : "login_success"
                  )
                }
              } catch (error) {
                console.error(error)
                reject(error)
              } finally {
                commit("setSSOLoading", false)
              }
            })
          } else {
            commit("setSSOLoading", false)
          }
        },
        { scope: "email" }
      )
    })
  },
  linkFacebook ({ dispatch, commit }) {
    commit("setSSOLinkError", "")
    window.FB.login(
      async function (response) {
        if (response.authResponse) {
          window.FB.getLoginStatus(async function (response) {
            const token = response.authResponse.accessToken
            const body = new FormData()
            body.append("token", token)
            try {
              const config = {
                headers: getAuthHeaders(),
              }
              const res = await axios.post(
                `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/facebook/connect.php`,
                body,
                config
              )
              if (res.data.status === "success") {
                MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
                  platform_name: "Facebook",
                })
                await dispatch("oauthLogin", {
                  provider: "facebook",
                  token,
                })
              }
              dispatch("checkAuthStatus")
            } catch (error) {
              console.error(error)
              dispatch("checkAuthStatus")
              commit("setSSOLinkError", error.response.data.error)
            }
          })
        }
      },
      { scope: "email" }
    )
  },
  async linkGoogle ({ dispatch, commit }) {
    commit("setSSOLinkError", "")
    let token
    let body
    try {
      const googleUser = await Vue.prototype.$gAuth.signIn()
      const authResponse = googleUser.getAuthResponse()
      token = authResponse.id_token
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.message)
    }
    try {
      const config = {
        headers: getAuthHeaders(),
      }
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/google/connect.php`,
        body,
        config
      )
      if (res.data.status === "success") {
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Google",
        })
        await dispatch("oauthLogin", {
          provider: "google",
          token,
        })
      } else {
        commit(
          "setSSOLinkError",
          "Something went wrong, please contact customer support if you need help"
        )
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.error)
    }
  },
  async linkApple ({ dispatch, commit }) {
    commit("setSSOLinkError", "")
    let token
    let body
    try {
      const data = await window.AppleID.auth.signIn()
      token = data.authorization.id_token
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.message)
    }
    try {
      const config = {
        headers: getAuthHeaders(),
      }
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/apple/connect.php`,
        body,
        config
      )
      if (res.data.status === "success") {
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Apple",
        })
        await dispatch("oauthLogin", {
          provider: "apple",
          token,
        })
      } else {
        commit(
          "setSSOLinkError",
          "Something went wrong, please contact customer support if you need help"
        )
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.error)
    }
  },
  beginDiscordSSO () {
    openWindow(
      `http://discord.com/api/oauth2/authorize?client_id=${DISCORD_SSO_OPTIONS.clientId}&redirect_uri=${DISCORD_SSO_OPTIONS.redirectURI}&response_type=${DISCORD_SSO_OPTIONS.responseType}&scope=${DISCORD_SSO_OPTIONS.responseScope}`,
      "Discord"
    )
  },
  async linkDiscord ({ dispatch, commit }, { token }) {
    commit("setSSOLinkError", "")
    let body
    try {
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.message)
    }
    try {
      const config = {
        headers: getAuthHeaders(),
      }
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/discord/connect.php`,
        body,
        config
      )
      if (res.data.status === "success") {
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Discord",
        })
        await dispatch("oauthLogin", {
          provider: "discord",
          token,
        })
      } else {
        commit(
          "setSSOLinkError",
          "Something went wrong, please contact customer support if you need help"
        )
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.error)
    }
  },
  beginAimLabSSO () {
    openWindow(
      `${AIM_LAB_SSO_OPTIONS.oauthEndpoint}/auth?client_id=${AIM_LAB_SSO_OPTIONS.clientId}&redirect_uri=${AIM_LAB_SSO_OPTIONS.redirectURI}&response_type=${AIM_LAB_SSO_OPTIONS.responseType}&scope=${AIM_LAB_SSO_OPTIONS.responseScope}`,
      "Aim Lab"
    )
  },
  async linkAimLab ({ dispatch, commit }, { token, refreshToken }) {
    commit("setSSOLinkError", "")
    let body
    try {
      body = new FormData()
      body.append("token", token)
      body.append("refresh_token", refreshToken)
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.message)
    }
    try {
      const config = {
        headers: getAuthHeaders(),
      }
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/aim-lab/connect.php`,
        body,
        config
      )
      if (res.data.status === "success") {
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Aim Lab",
        })
        await dispatch("oauthLogin", {
          provider: "aim-lab",
          token,
        })
      } else {
        commit(
          "setSSOLinkError",
          "Something went wrong, please contact customer support if you need help"
        )
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLinkError", error.response.data.error)
    }
  },
  async loginWithGoogle ({ dispatch, commit }) {
    commit("setSSOLoading", true)
    let token
    let body
    try {
      const googleUser = await Vue.prototype.$gAuth.signIn()
      const authResponse = googleUser.getAuthResponse()
      token = authResponse.id_token
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      commit("setSSOLoading", false)
    }
    try {
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/google/connect.php`,
        body
      )
      const tokens = {
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      }
      await dispatch("initTokens", tokens)
      if (res.data.status === "success") {
        await dispatch("trackUserLoginEvent")
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Google",
        })
        // await dispatch("oauthLogin", {
        //   provider: "google",
        //   token,
        // })
        await Promise.all([
          dispatch("User/loadUser", null, { root: true }),
          dispatch("User/loadUserCapabilities", null, { root: true }),
          dispatch("User/Profile/loadSettings", null, { root: true }),
        ])
        commit("setSSOLoading", false)
        if (res.data.user_id) return "register_success"
        return "login_success"
      } else {
        commit("setSSOLoading", false)
        MIXINS.methods.trackAnalyticsEvent("Return SSO error")
        return "fail"
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLoading", false)
      MIXINS.methods.trackAnalyticsEvent("Return SSO error")
      return "fail"
    }
  },
  async loginWithApple ({ dispatch, commit }) {
    commit("setSSOLoading", true)
    let token
    let body
    try {
      const data = await window.AppleID.auth.signIn()
      token = data.authorization.id_token
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      commit("setSSOLoading", false)
    }

    try {
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/apple/connect.php`,
        body
      )
      const tokens = {
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      }
      await dispatch("initTokens", tokens)
      if (res.data.status === "success") {
        await dispatch("trackUserLoginEvent")
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Apple",
        })
        // await dispatch("oauthLogin", {
        //   provider: "apple",
        //   token,
        // })
        await Promise.all([
          dispatch("User/loadUser", null, { root: true }),
          dispatch("User/loadUserCapabilities", null, { root: true }),
          dispatch("User/Profile/loadSettings", null, { root: true }),
        ])
        commit("setSSOLoading", false)

        if (res.data.user_id) return "register_success"
        return "login_success"
      } else {
        commit("setSSOLoading", false)
        MIXINS.methods.trackAnalyticsEvent("Return SSO error")
        return "fail"
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLoading", false)
      MIXINS.methods.trackAnalyticsEvent("Return SSO error")
      return "fail"
    }
  },
  async loginWithDiscord ({ dispatch, commit }, token) {
    commit("setSSOLoading", true)
    let body
    try {
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      commit("setSSOLoading", false)
    }

    try {
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/discord/connect.php`,
        body
      )
      const tokens = {
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      }
      await dispatch("initTokens", tokens)
      if (res.data.status === "success") {
        await dispatch("trackUserLoginEvent")
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Discord",
        })
        // await dispatch("oauthLogin", {
        //   provider: "discord",
        //   token,
        // })
        await Promise.all([
          dispatch("User/loadUser", null, { root: true }),
          dispatch("User/loadUserCapabilities", null, { root: true }),
          dispatch("User/Profile/loadSettings", null, { root: true }),
        ])
        commit("setSSOLoading", false)

        if (res.data.user_id) return "register_success"
        return "login_success"
      } else {
        commit("setSSOLoading", false)
        MIXINS.methods.trackAnalyticsEvent("Return SSO error")
        return "fail"
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLoading", false)
      MIXINS.methods.trackAnalyticsEvent("Return SSO error")
      return "fail"
    }
  },
  async loginWithAimLab ({ dispatch, commit }, token) {
    commit("setSSOLoading", true)
    let body
    try {
      body = new FormData()
      body.append("token", token)
    } catch (error) {
      commit("setSSOLoading", false)
    }

    try {
      const res = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/single-sign-ons/aim-lab/connect.php`,
        body
      )
      const tokens = {
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      }
      await dispatch("initTokens", tokens)
      if (res.data.status === "success") {
        await dispatch("trackUserLoginEvent")
        MIXINS.methods.trackAnalyticsEvent("SSO Connection Authenticated", {
          platform_name: "Aim Lab",
        })
        // await dispatch("oauthLogin", {
        //   provider: "discord",
        //   token,
        // })

        await Promise.all([
          dispatch("User/loadUser", null, { root: true }),
          dispatch("User/loadUserCapabilities", null, { root: true }),
          dispatch("User/Profile/loadSettings", null, { root: true }),
        ])
        commit("setSSOLoading", false)

        if (res.data.user_id) return "register_success"
        return "login_success"
      } else {
        commit("setSSOLoading", false)
        MIXINS.methods.trackAnalyticsEvent("Return SSO error")
        return "fail"
      }
    } catch (error) {
      console.error(error)
      commit("setSSOLoading", false)
      MIXINS.methods.trackAnalyticsEvent("Return SSO error")
      return "fail"
    }
  },
  async refreshAimlabsToken ({ dispatch }) {
    try {
      await refreshAimlabsToken()
      dispatch("checkAuthStatus")
    } catch (e) {
      if (e.request?.status === 403) {
        dispatch("beginAimLabSSO")
      }
    }
  },
  bindAimlabsAuthListener ({ state, commit, dispatch }) {
    if (state.aimlabsAuthListenerBound || !window) {
      return
    }

    window.addEventListener("message", async (e) => {
      if (e.origin !== window.origin || !e.data.token) {
        return
      }

      if (e.data.type === "aim-lab") {
        await dispatch("linkAimLab", {
          token: e.data.token,
          refreshToken: e.data.refreshToken,
        })
        await dispatch("checkAuthStatus")
        await dispatch("processAimlabsAuthListenerQueue")
      }
    }, false)

    commit("setAimlabsAuthListenerBound", true)
  },
  addCallbackToAimlabsAuthListenerQueue ({ commit }, callback) {
    commit("addCallbackToAimlabsAuthListenerQueue", callback)
  },
  processAimlabsAuthListenerQueue ({ state, commit }) {
    // Clear out the callback queue awaiting token refresh
    const callbacks = state.aimlabsAuthListenerCallbackQueue
    callbacks.forEach((callback) => {
      if (typeof callback === "function") {
        callback()
      }
    })

    commit("clearAimlabsAuthListenerQueue")
  },
  resetAuth ({ commit }) {
    commit("setLoading", false)
    commit("setError", "")
  },
  async login ({ commit, dispatch }, { email, password, rememberMe }) {
    commit("setLoading", true)
    try {
      const body = new FormData()
      body.append("email", email)
      body.append("password", password)
      body.append("remember_me", rememberMe ? 1 : 0)
      const response = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/login.php`,
        body
      )
      const tokens = {
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      }
      await dispatch("initTokens", tokens)

      const status = response.data.status
      if (status === "success") {
        try {
          await dispatch("trackUserLoginEvent")
          // await apiLogin(email, password, rememberMe)
          await Promise.all([
            dispatch("User/loadUser", null, { root: true }),
            dispatch("User/loadUserCapabilities", null, { root: true }),
            dispatch("User/Profile/loadSettings", null, { root: true }),
          ])
          return "success"
        } catch (error) {
          console.error(error)
          commit("setLoading", false)
          return "fail"
        }
      } else {
        let error = response.data.error || "Server error, try again."
        commit("setError", error)
        commit("setLoading", false)
        return "fail"
      }
    } catch (error) {
      console.error(error)
      commit("setLoading", false)
      return "fail"
    }
  },
  async register ({ commit, dispatch }, { email, password, username, optInConsent }) {
    commit("setLoading", true)
    try {
      const body = new FormData()
      body.append("email", email)
      if (password) {
        body.append("password", password)
      }
      if (username) {
        body.append("username", username)
      }
      const response = await axios.post(
        `${process.env.PROGUIDES_WEB_URL}functions/registration/register.php`,
        body
      )
      const tokens = {
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      }
      await dispatch("initTokens", tokens)
      const status = response.data.status
      if (status === "success") {
        try {
          // await apiLogin(email, password, true)
          const [user] = await Promise.all([
            dispatch("User/loadUser", null, { root: true }),
            dispatch("User/loadUserCapabilities", null, { root: true }),
            dispatch("User/Profile/loadSettings", null, { root: true }),
          ])

          await dispatch("trackUserRegistrationEvent", { userId: user?.id, email, username, optInConsent })
          return "success"
        } catch (error) {
          console.error(error)
          commit("setLoading", false)
          return "fail"
        }
      } else {
        let error = response.data.error || "Server error, try again."
        commit("setError", error)
        commit("setLoading", false)
        return "fail"
      }
    } catch (error) {
      console.error(error)
      commit("setLoading", false)
      return "fail"
    }
  },
  trackUserLoginEvent () {
    // UserLeap: Track 'registered' on Login
    let anonymousUserId = window.localStorage.getItem("anonymous_user_id")
    if (!anonymousUserId) {
      anonymousUserId = uuidv4()
      window.localStorage.setItem("anonymous_user_id", anonymousUserId)
    }
    // eslint-disable-next-line no-undef
    UserLeap("setUserId", anonymousUserId)
    // eslint-disable-next-line no-undef
    UserLeap("track", "Sign Up")
    // Analytics
    MIXINS.methods.trackAnalyticsEvent("login", {
      method: "Email",
    })
  },
  async trackUserRegistrationEvent ({ dispatch }, { userId, email, username, optInConsent }) {
    // FB Pixel
    dispatch(
      "Tracking/fbQuery",
      { event: "CompleteRegistration" },
      { root: true }
    )

    dispatch(
      "Tracking/analyticsConversion",
      { conversionName: "Sign-up" },
      { root: true }
    )

    // Reddit Pixel
    dispatch(
      "Tracking/trackRedditPixelEvent",
      { event: "SignUp" },
      { root: true }
    )

    // Analytics
    MIXINS.methods.trackAnalyticsIdentity(userId, {
      email,
      username,
    })
    // track whether user should be subscribed to marketing emails
    MIXINS.methods.trackAnalyticsGroup(process.env.BRAZE_GROUP_SUBSCRIPTION_KEY, {
      email,
      subscriptionState: optInConsent ? "subscribed" : "unsubscribed",
    })
    MIXINS.methods.trackAnalyticsEvent("Register from Homepage")
    // Analytics
    dispatch("Tracking/analyticsEvent", {
      event: "sign_up",
      properties: {
        method: "Email",
      },
    }, { root: true })

    // UserLeap: Track Account Creation
    let anonymousUserId = window.localStorage.getItem("anonymous_user_id")
    if (!anonymousUserId) {
      anonymousUserId = uuidv4()
      window.localStorage.setItem("anonymous_user_id", anonymousUserId)
    }
    // eslint-disable-next-line no-undef
    UserLeap("setUserId", anonymousUserId)
    // eslint-disable-next-line no-undef
    UserLeap("track", "Sign Up")
  },
  async oauthLogin (context, { provider, token }) {
    const data = await oauthLogin(provider, token)
    let clientCredentials = data.access_token
    window.localStorage.setItem("client_credentials", clientCredentials)
    let refreshToken = data.refresh_token
    window.localStorage.setItem("refresh_token", refreshToken)
  },
  async checkAuthStatus ({ commit }) {
    const data = await checkAuthStatus()
    if (data?.oauth_providers) {
      commit("setOauthProviders", data.oauth_providers)
    }
  },
  async disconnectOauth ({ dispatch }, body) {
    const data = await disconnectOauth(body)
    dispatch("checkAuthStatus")
    return data
  },
  async fetchClientCredentialsWithOneTimeToken ({ dispatch }, oneTimeToken) {
    const data = await fetchClientCredentialsWithOneTimeToken(oneTimeToken)
    const token = data?.access_token
    if (token) {
      window.localStorage.setItem("client_credentials", token)
      dispatch("User/loadUser", null, { root: true })
    }
    const refreshToken = data?.refresh_token
    if (refreshToken) {
      window.localStorage.setItem("refresh_token", refreshToken)
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/auth/auth.module.js